<template>
  <div class="min-h-screen bg-white">
    <!-- Header with Navigation -->
    <div class="flex justify-between items-center w-full bg-white px-6 py-4 border-b">
      <div class="flex items-center space-x-6">
        <h1 class="text-2xl font-bold text-black">Admin Dashboard</h1>
        <nav class="flex space-x-4">
          <router-link
            to="/admin/dashboard"
            class="text-black hover:text-purple-600"
            :class="{ 'text-purple-600 font-semibold': $route.path === '/admin/dashboard' }"
          >
            Dashboard
          </router-link>
          <router-link
            to="/admin/transactions"
            class="text-black hover:text-purple-600"
            :class="{ 'text-purple-600 font-semibold': $route.path === '/admin/transactions' }"
          >
            Transactions
          </router-link>
          <router-link
            to="/admin/change-password"
            class="text-black hover:text-purple-600"
            :class="{ 'text-purple-600 font-semibold': $route.path === '/admin/change-password' }"
          >
            Change Password
          </router-link>
        </nav>
      </div>
      <button @click="logout" class="btn btn-ghost text-black">Logout</button>
    </div>

    <div class="p-6 space-y-6">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Today's Stats -->
        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-semibold mb-4 text-black">Today's Sales</h2>
          <div class="stats shadow w-full">
            <div class="stat">
              <div class="stat-title text-black">Amount (INR)</div>
              <div class="stat-value text-black">₹{{ (stats.today.inr || 0).toFixed(2) }}</div>
            </div>
            <div class="stat">
              <div class="stat-title text-black">LRS Coins</div>
              <div class="stat-value text-black">{{ (stats.today.lrs || 0).toFixed(4) }}</div>
            </div>
          </div>
        </div>

        <!-- Lifetime Stats -->
        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-semibold mb-4 text-black">Lifetime Sales</h2>
          <div class="stats shadow w-full">
            <div class="stat">
              <div class="stat-title text-black">Amount (INR)</div>
              <div class="stat-value text-black">₹{{ (stats.lifetime.inr || 0).toFixed(2) }}</div>
            </div>
            <div class="stat">
              <div class="stat-title text-black">LRS Coins</div>
              <div class="stat-value text-black">{{ (stats.lifetime.lrs || 0).toFixed(4) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Transactions -->
      <div class="bg-white rounded-lg shadow">
        <div class="p-6">
          <h2 class="text-xl font-semibold mb-4 text-black">Recent Transactions</h2>
          <div class="overflow-x-auto">
            <table class="table w-full">
              <thead>
                <tr>
                  <th class="text-black bg-gray-50">Transaction ID</th>
                  <th class="text-black bg-gray-50">Amount</th>
                  <th class="text-black bg-gray-50">LRS</th>
                  <th class="text-black bg-gray-50">Wallet</th>
                  <th class="text-black bg-gray-50">Payment ID</th>
                  <th class="text-black bg-gray-50">Status</th>
                  <th class="text-black bg-gray-50">Blockchain TX</th>
                  <th class="text-black bg-gray-50">Date</th>
                  <th class="text-black bg-gray-50">Actions</th>
                </tr>
              </thead>
              <tbody class="text-black">
                <tr v-for="tx in stats.recentTransactions" :key="tx.id" class="hover:bg-gray-50">
                  <td class="font-mono text-sm">{{ tx.id }}</td>
                  <td>₹{{ (tx.amount || 0).toFixed(2) }}</td>
                  <td>{{ (tx.lrs_amount || 0).toFixed(4) }}</td>
                  <td class="font-mono text-xs" :title="tx.wallet_address">
                    {{ tx.short_wallet }}
                  </td>
                  <td class="font-mono text-xs">{{ tx.upi_transaction_id || '-' }}</td>
                  <td>
                    <span :class="{
                      'px-2 py-1 rounded text-sm': true,
                      'bg-yellow-100 text-yellow-800': tx.status === 'Pending',
                      'bg-green-100 text-green-800': tx.status === 'Completed',
                      'bg-red-100 text-red-800': tx.status === 'Rejected'
                    }" :title="tx.status === 'Rejected' ? `Reason: ${tx.rejection_reason}` : ''">
                      {{ tx.status }}
                    </span>
                  </td>
                  <td class="font-mono text-xs">
                    <a
                      v-if="tx.tx_hash"
                      :href="`${explorerUrl}/tx/${tx.tx_hash}`"
                      target="_blank"
                      class="text-purple-600 hover:text-purple-800 hover:underline"
                      :title="tx.tx_hash"
                    >
                      {{ tx.tx_hash.substring(0, 6) + '...' + tx.tx_hash.slice(-4) }}
                    </a>
                    <span v-else>-</span>
                  </td>
                  <td class="text-sm">
                    {{ new Date(tx.created_at).toLocaleString() }}
                  </td>
                  <td>
                    <div class="flex gap-2">
                      <button
                        v-if="tx.screenshot_path"
                        class="btn btn-xs btn-ghost"
                        @click="viewScreenshot(tx.screenshot_path)"
                        title="View Screenshot"
                      >
                        🖼️
                      </button>
                      <button
                        v-if="tx.status === 'Pending'"
                        class="btn btn-xs btn-success"
                        @click="updateStatus(tx.id, 'completed')"
                        title="Approve"
                      >
                        ✓
                      </button>
                      <button
                        v-if="tx.status === 'Pending'"
                        class="btn btn-xs btn-error"
                        @click="openRejectModal(tx.id)"
                        title="Reject"
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="stats.recentTransactions.length === 0">
                  <td colspan="8" class="text-center py-4 text-gray-500">
                    No transactions found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Rejection Modal -->
    <div v-if="showRejectModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4 text-black">Reject Transaction</h3>
        <div class="form-control">
          <label class="label">
            <span class="label-text text-black">Reason for Rejection</span>
          </label>
          <textarea
            v-model="rejectionReason"
            class="textarea textarea-bordered h-24 text-black"
            placeholder="Please provide a reason for rejecting this transaction"
            required
          ></textarea>
        </div>
        <div class="flex justify-end space-x-3 mt-4">
          <button
            class="btn btn-ghost"
            @click="closeRejectModal"
          >
            Cancel
          </button>
          <button
            class="btn btn-error"
            :disabled="!rejectionReason.trim()"
            @click="confirmReject"
          >
            Reject Transaction
          </button>
        </div>
      </div>
    </div>

    <!-- Screenshot Modal -->
    <div v-if="showScreenshotModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white p-4 rounded-lg shadow-lg max-w-4xl w-full mx-4 relative">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-black">Transaction Screenshot</h3>
          <button
            class="btn btn-sm btn-ghost text-black hover:text-gray-700"
            @click="closeScreenshotModal"
          >
            ✕
          </button>
        </div>
        <div class="relative w-full" style="height: 80vh;">
          <img
            v-if="!imageError"
            :src="`${currentScreenshot}`"
            alt="Transaction Screenshot"
            class="w-full h-full object-contain"
            @error="handleImageError"
            @load="imageError = false"
          />
          <div v-if="imageError" class="absolute inset-0 flex flex-col items-center justify-center">
            <div class="text-red-600 mb-2">Failed to load image</div>
            <button
              class="btn btn-sm btn-ghost"
              @click="retryLoadImage"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Approval Modal -->
    <ApprovalModal
      v-if="showApprovalModal"
      :show="showApprovalModal"
      :transaction="selectedTransaction"
      @close="closeApprovalModal"
      @completed="handleApprovalComplete"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import ApprovalModal from './ApprovalModal.vue'
import useEnv from '../composables/useEnv'

const router = useRouter()
const { apiUrl, explorerUrl } = useEnv()
const stats = ref({
  today: { inr: 0, lrs: 0, count: 0 },
  lifetime: { inr: 0, lrs: 0, count: 0 },
  recentTransactions: []
})

const showRejectModal = ref(false)
const rejectionReason = ref('')
const selectedTransactionId = ref('')
const showScreenshotModal = ref(false)
const currentScreenshot = ref('')
const imageError = ref(false)
const showApprovalModal = ref(false)
const selectedTransaction = ref(null)

const fetchDashboardData = async () => {
  try {
    const token = localStorage.getItem('adminToken')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${apiUrl}/api/admin/dashboard`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      throw new Error('Failed to fetch dashboard data')
    }

    const data = await response.json()

    // Ensure numbers are properly converted
    stats.value = {
      today: {
        inr: Number(data.today.inr) || 0,
        lrs: Number(data.today.lrs) || 0,
        count: Number(data.today.count) || 0
      },
      lifetime: {
        inr: Number(data.lifetime.inr) || 0,
        lrs: Number(data.lifetime.lrs) || 0,
        count: Number(data.lifetime.count) || 0
      },
      recentTransactions: data.recentTransactions.map(tx => ({
        ...tx,
        amount: Number(tx.amount),
        lrs_amount: Number(tx.lrs_amount)
      }))
    }
  } catch (error) {
    console.error('Error fetching dashboard data:', error)
    alert('Failed to fetch dashboard data')
  }
}

const logout = () => {
  localStorage.removeItem('adminToken')
  router.push('/admin/login')
}

const viewScreenshot = (path: string) => {
  if (!path.startsWith('http')) {
    path = `${apiUrl}${path}`;
  }
  imageError.value = false;
  currentScreenshot.value = path;
  showScreenshotModal.value = true;
}

const closeScreenshotModal = () => {
  showScreenshotModal.value = false
  currentScreenshot.value = ''
  imageError.value = false
}

const openRejectModal = (txId: string) => {
  selectedTransactionId.value = txId
  showRejectModal.value = true
  rejectionReason.value = ''
}

const closeRejectModal = () => {
  showRejectModal.value = false
  rejectionReason.value = ''
  selectedTransactionId.value = ''
}

const confirmReject = async () => {
  if (!rejectionReason.value.trim()) {
    alert('Please provide a reason for rejection')
    return
  }

  try {
    const token = localStorage.getItem('adminToken')
    const response = await fetch(`${apiUrl}/api/admin/transaction/${selectedTransactionId.value}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        status: 'rejected',
        reason: rejectionReason.value
      })
    })

    if (response.ok) {
      await fetchDashboardData() // Refresh the data
      closeRejectModal()
    } else {
      alert('Failed to update transaction status')
    }
  } catch (error) {
    console.error('Error updating status:', error)
    alert('Failed to update status')
  }
}

const updateStatus = async (txId: string, status: string) => {
  if (status === 'rejected') {
    openRejectModal(txId)
    return
  }

  if (status === 'completed') {
    const transaction = stats.value.recentTransactions.find(tx => tx.id === txId)
    if (transaction) {
      selectedTransaction.value = transaction
      showApprovalModal.value = true
    }
    return
  }

  if (!confirm(`Are you sure you want to ${status} this transaction?`)) {
    return
  }

  try {
    const token = localStorage.getItem('adminToken')
    const response = await fetch(`${apiUrl}/api/admin/transaction/${txId}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ status })
    })

    if (response.ok) {
      await fetchDashboardData() // Refresh the data
    } else {
      alert('Failed to update transaction status')
    }
  } catch (error) {
    console.error('Error updating status:', error)
    alert('Failed to update status')
  }
}

const retryLoadImage = () => {
  imageError.value = false;
  // Force image reload by adding timestamp
  currentScreenshot.value = `${currentScreenshot.value}?t=${Date.now()}`;
};

const handleImageError = () => {
  console.error('Failed to load image:', currentScreenshot.value);
  imageError.value = true;
};

const closeApprovalModal = () => {
  showApprovalModal.value = false
  selectedTransaction.value = null
}

const handleApprovalComplete = async () => {
  await fetchDashboardData()
  closeApprovalModal()
}

onMounted(() => {
  fetchDashboardData()
  // Refresh data every minute
  setInterval(fetchDashboardData, 60000)
})
</script>

<style scoped>
/* Remove default table borders */
:deep(.table) {
  --tw-border-spacing-x: 0;
  --tw-border-spacing-y: 0;
}

:deep(.table th),
:deep(.table td) {
  border: none;
  padding: 1rem;
}

:deep(.table tr) {
  border-bottom: 1px solid #f3f4f6;
}

/* Clean shadow */
.shadow {
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
}

/* Force white background */
.bg-white {
  background-color: white !important;
}

/* Force black text globally within the dashboard */
:deep(*) {
  color: black;
}

/* Exceptions for buttons that need white text */
:deep(.btn-primary),
:deep(.btn-error),
:deep(.btn-success) {
  color: white !important;
}

/* Make modal text black */
.modal-text {
  color: black !important;
}

/* Remove unwanted margins/padding */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* Add these styles */
.textarea {
  background-color: white !important;
  border: 1px solid #e5e7eb !important;
  color: black !important;
}

.textarea:focus {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 1px #3b82f6 !important;
}

/* Add these styles */
.object-contain {
  object-fit: contain;
}

/* Add these styles */
.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

/* Optional: Add tooltip for full hash on hover */
[title] {
  position: relative;
  cursor: pointer;
}
</style>